/**
 * The Praxadon mark.
 *
 * A solid **P** whose counter is a play triangle: the letter and the run button in one
 * shape. It was chosen over the alternatives for what it does at twenty-four pixels in
 * a toolbar — the triangle still reads, and the letter still reads, which is the only
 * test a product mark has to pass.
 *
 * Drawn inline rather than loaded as an image so it costs no request, inherits the
 * page's colours where it should, and stays crisp at any size. The gradient's id is
 * per-instance: two marks on one page must not fight over it.
 */

import { useId } from 'react';

import { cn } from '@praxadon/ui';

export interface BrandMarkProps {
  /** Pixels. The mark is square. */
  size?: number;
  className?: string;
}

export function BrandMark({ size = 32, className }: BrandMarkProps) {
  const id = useId();
  const gradient = `brand-${id}`;

  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      role="img"
      aria-label="Praxadon"
      className={cn('shrink-0', className)}
    >
      <defs>
        <linearGradient id={gradient} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#6366f1" />
          <stop offset="1" stopColor="#a855f7" />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="9" fill={`url(#${gradient})`} />
      <path d="M9.4 25.4V7.2h7.4a6 6 0 0 1 0 12h-3.6v6.2z" fill="#fff" />
      <path d="M15.4 10.4v5.6l4.76-2.8z" fill={`url(#${gradient})`} />
    </svg>
  );
}

/** The mark with the name beside it, for a header or a sign-in page. */
export function BrandLockup({
  size = 28,
  subdued = false,
  className,
}: {
  size?: number;
  /** On a busy background, where the name should not shout. */
  subdued?: boolean;
  className?: string;
}) {
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <BrandMark size={size} />
      <span
        className={cn(
          'font-semibold tracking-tight',
          subdued ? 'text-fg-muted' : 'text-fg',
          size >= 28 ? 'text-lg' : 'text-sm',
        )}
      >
        Praxadon
      </span>
    </span>
  );
}
